import { createContext, useReducer, useContext, ReactNode } from "react";
import { ConnectionCreateInfo, EasyflexFinancialAdministration } from "../types";

const initialApplicationInfo: ConnectionCreateInfo = {
  availableEasyFlexAdmins: [],
  availableDestinations: [],
  availableEasyVATCodes: [],
  availableDestinationVATCodes: [],
  availableModules: [],
  availableJournals: [],
  easyflexFinancialAdmin: null,
};

type ApplicationInfoAction =
  | { type: "UPDATE_EASYFLEX_ADMINS"; admins: ConnectionCreateInfo["availableEasyFlexAdmins"] }
  | { type: "UPDATE_DESTINATIONS"; destinations: ConnectionCreateInfo["availableDestinations"] }
  | { type: "UPDATE_EASYFLEX_VAT_CODES"; codes: ConnectionCreateInfo["availableEasyVATCodes"] }
  | { type: "UPDATE_DESTINATION_VAT_CODES"; codes: ConnectionCreateInfo["availableDestinationVATCodes"] }
  | { type: "UPDATE_MODULES"; modules: ConnectionCreateInfo["availableModules"] }
  | { type: "UPDATE_JOURNALS"; journals: ConnectionCreateInfo["availableJournals"] }
  | { type: "UPDATE_EASYFLEX_FINANCIAL_ADMIN"; data: EasyflexFinancialAdministration | null };

const applicationInfoReducer = (state: ConnectionCreateInfo, action: ApplicationInfoAction): ConnectionCreateInfo => {
  let newState;

  switch (action.type) {
    case "UPDATE_EASYFLEX_ADMINS":
      newState = { ...state, availableEasyFlexAdmins: action.admins };
      break;
    case "UPDATE_DESTINATIONS":
      newState = { ...state, availableDestinations: action.destinations };
      break;
    case "UPDATE_EASYFLEX_VAT_CODES":
      newState = { ...state, availableEasyVATCodes: action.codes };
      break;
    case "UPDATE_DESTINATION_VAT_CODES":
      newState = { ...state, availableDestinationVATCodes: action.codes };
      break;
    case "UPDATE_MODULES":
      newState = { ...state, availableModules: action.modules };
      break;
    case "UPDATE_JOURNALS":
      newState = { ...state, availableJournals: action.journals };
      break;
    case "UPDATE_EASYFLEX_FINANCIAL_ADMIN":
      newState = { ...state, easyflexFinancialAdmin: action.data };
      break;
    default:
      throw new Error("Invalid action type");
  }

  return newState;
}

interface ApplicationInfoContextType {
  applicationInfo: ConnectionCreateInfo;
  dispatch: React.Dispatch<ApplicationInfoAction>;
}

// Create Context
const ApplicationInfoContext = createContext<ApplicationInfoContextType | undefined>(undefined);

export const ApplicationInfoProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(applicationInfoReducer, initialApplicationInfo);

  return (
    <ApplicationInfoContext.Provider value={{ applicationInfo: state, dispatch }}>
      {children}
    </ApplicationInfoContext.Provider>
  );
}

// Custom Hook
// eslint-disable-next-line react-refresh/only-export-components -- hook is colocated with the component or provider that uses it
export const useApplicationInfoContext = () => {
  const context = useContext(ApplicationInfoContext);
  if (!context) {
    throw new Error("useApplicationInfoContext must be used within a ApplicationInfoProvider");
  }
  return context;
}

export default ApplicationInfoContext;
